import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth } from '../../firebase';
import { sendPasswordResetEmail, signOut } from 'firebase/auth';
import { useAuth } from '../../context/AuthContext';
import './UserProfileModal.css';

const API = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_API_BASE_URL || `http://${window.location.hostname}:5001`);

const UserProfileModal = ({ onClose }) => {
  const { currentUser, isPremium, userData } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isDashboard = location.pathname.startsWith('/dashboard');
  const [resetSent, setResetSent] = useState(false);
  const [error, setError] = useState('');

  const [subscription, setSubscription] = useState({
    plan: isPremium ? 'Cospyria Pro (Monthly)' : 'Free Tier',
    status: isPremium ? 'Active' : 'Inactive',
    nextBilling: 'N/A',
  });
  const [transactions, setTransactions] = useState([]);
  const [loadingData, setLoadingData] = useState(false);

  // Extract user info safely
  const userName = currentUser?.displayName || 'Cosmic Explorer';
  const userEmail = currentUser?.email || 'No email attached';
  const userInitial = userName.charAt(0).toUpperCase();

  useEffect(() => {
    if (isPremium && userData?.lastSubscriptionId) {
      setLoadingData(true);
      fetch(`${API}/api/payments/subscription-info/${userData.lastSubscriptionId}`)
        .then(res => res.json())
        .then(data => {
          if (data.success) {
            setSubscription({
              plan: 'Cospyria Pro (Monthly)',
              status: data.subscription.status,
              nextBilling: data.subscription.nextBillingDate ? new Date(data.subscription.nextBillingDate * 1000).toLocaleDateString() : 'N/A'
            });
            const formattedTx = data.transactions.map(tx => ({
              ...tx,
              date: new Date(tx.date * 1000).toLocaleDateString(),
              status: tx.status.charAt(0).toUpperCase() + tx.status.slice(1)
            }));
            setTransactions(formattedTx);
          }
        })
        .catch(err => console.error("Failed to fetch subscription info:", err))
        .finally(() => setLoadingData(false));
    }
  }, [isPremium, userData]);

  const handleResetPassword = async () => {
    if (!currentUser?.email) {
      setError('No email address found for this account.');
      return;
    }

    const isGoogleUser = currentUser?.providerData?.some(
      (provider) => provider.providerId === 'google.com'
    );

    if (isGoogleUser) {
      setError('You are signed in with Google. Password reset is not available.');
      return;
    }

    try {
      await sendPasswordResetEmail(auth, currentUser.email);
      setResetSent(true);
      setTimeout(() => setResetSent(false), 5000);
    } catch (err) {
      setError('Failed to send reset email. Please try again later.');
      console.error(err);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      localStorage.removeItem('kundliData');
      localStorage.removeItem('kundliChartData');
      window.location.href = '/';
    } catch (err) {
      setError('Failed to sign out. Please try again.');
      console.error(err);
    }
  };

  const handleNavigation = () => {
    if (isDashboard) {
      navigate('/');
      onClose();
    } else {
      navigate('/dashboard');
      onClose();
    }
  };

  return (
    <div className="profile-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="profile-modal-content">

        {/* Header */}
        <div className="profile-modal-header">
          <h2>👤 User Profile</h2>
          <button className="profile-close-btn" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {/* Body */}
        <div className="profile-modal-body">

          {/* User Info */}
          <div className="profile-user-info">
            <div className="profile-avatar">{userInitial}</div>
            <div className="profile-details">
              <h3>{userName}</h3>
              <p>{userEmail}</p>
            </div>
          </div>

          {/* Subscription Status */}
          <div className="profile-plan-card">
            <div className="plan-info">
              <h4>{subscription.plan}</h4>
              <p>Next billing: {subscription.nextBilling}</p>
            </div>
            <div className="plan-status">{subscription.status}</div>
          </div>

          {/* Transaction History */}
          <div className="profile-transactions">
            <h4>Transaction History</h4>
            {loadingData ? (
              <p style={{textAlign: 'center', margin: '1rem', color: '#ccc'}}>Loading...</p>
            ) : transactions.length === 0 ? (
              <p style={{textAlign: 'center', margin: '1rem', color: '#ccc'}}>No transactions yet.</p>
            ) : (
              <table className="transactions-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((tx) => (
                    <tr key={tx.id}>
                      <td data-label="Date">{tx.date}</td>
                      <td data-label="Amount">{tx.amount}</td>
                      <td data-label="Status" className={tx.status === 'Success' ? 'tx-status-success' : 'tx-status-pending'}>{tx.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Messages */}
          {error    && <div className="profile-error-msg">{error}</div>}
          {resetSent && <div className="profile-success-msg">✅ Password reset email sent!<br/>(Check your spam/junk folder)</div>}

          {/* Actions */}
          <div className="profile-actions">
            <button className="profile-btn btn-home" onClick={handleNavigation}>
              {isDashboard ? '🏠 Home' : '📊 Dashboard'}
            </button>
            <button className="profile-btn btn-reset" onClick={handleResetPassword}>
              🔐 Reset Password
            </button>
            <button className="profile-btn btn-signout" onClick={handleSignOut}>
              🚪 Sign Out
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default UserProfileModal;
